import OwnerChat from "@/components/OwnerChat";
import React, {
  useEffect,
  useState,
} from "react";

import {
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  LayoutDashboard,
  Building2,
  Users,
  Calculator,
  Wallet,
  Upload,
  FileText,
  ReceiptText,
  BarChart3,
  FileBarChart,
  UserCog,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Menu,
  X,
  MoreHorizontal,
} from "lucide-react";

import {
  useAuth,
} from "@/context/AuthContext";

import {
  api,
} from "@/lib/api";

import {
  Logo,
  QrewHrLogo,
} from "@/components/Logo";


const NAV = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
  },
  {
    to: "/analytics",
    label: "Analytics",
    icon: BarChart3,
  },
  { to: "/enterprise", label: "Enterprise", icon: BarChart3 },
  {
    to: "/clients",
    label: "Clients",
    icon: Building2,
  },
  {
    to: "/employees",
    label: "Employees",
    icon: Users,
  },
  {
    to: "/attendance",
    label: "Attendance & Inputs",
    icon: ClipboardList,
  },
  {
    to: "/payroll",
    label: "Payroll",
    icon: Calculator,
  },
  {
    to: "/wage-masters",
    label: "Wage Masters",
    icon: Wallet,
  },
  {
    to: "/imports",
    label: "Imports",
    icon: Upload,
  },
  {
    to: "/payslips",
    label: "Payslips",
    icon: FileText,
  },
  {
    to: "/invoices",
    label: "Invoices",
    icon: ReceiptText,
  },
  {
    to: "/receivables",
    label: "Receivables",
    icon: Wallet,
  },
  {
    to: "/reports",
    label: "Reports",
    icon: FileBarChart,
  },
  {
    to: "/users",
    label: "Users",
    icon: UserCog,
    superOnly: true,
  },
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
  },
];


const MOBILE_PRIMARY = [
  {
    to: "/dashboard",
    label: "Home",
    icon: LayoutDashboard,
  },
  {
    to: "/clients",
    label: "Clients",
    icon: Building2,
  },
  {
    to: "/employees",
    label: "Employees",
    icon: Users,
  },
  {
    to: "/payroll",
    label: "Payroll",
    icon: Calculator,
  },
];


export default function AppLayout({
  children,
}) {
  const {
    user,
    logout,
    isSuper,
    selectedClient,
    setSelectedClient,
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const [
    clients,
    setClients,
  ] = useState([]);

  const [
    collapsed,
    setCollapsed,
  ] = useState(() => {
    try {
      return (
        sessionStorage.getItem(
          "nakshatra-sidebar-collapsed"
        ) === "1"
      );
    } catch {
      return false;
    }
  });

  const [
    mobileMore,
    setMobileMore,
  ] = useState(false);


  useEffect(() => {
    api
      .get("/clients")
      .then((r) =>
        setClients(r.data)
      )
      .catch(() => {});
  }, []);


  useEffect(() => {
    try {
      sessionStorage.setItem(
        "nakshatra-sidebar-collapsed",
        collapsed ? "1" : "0"
      );
    } catch {
      // Ignore browser storage restrictions.
    }
  }, [
    collapsed,
  ]);


  useEffect(() => {
    setMobileMore(false);
  }, [
    location.pathname,
  ]);


  const doLogout = async () => {
    await logout();
    navigate("/login");
  };


  const visibleNav =
    NAV.filter(
      (n) =>
        !n.superOnly ||
        isSuper
    );


  const moreNav =
    visibleNav.filter(
      (n) =>
        !MOBILE_PRIMARY.some(
          (m) => m.to === n.to
        )
    );


  const roleLabel =
    (
      user?.role ||
      ""
    ).replace(
      /_/g,
      " "
    );


  const initials =
    (
      user?.name ||
      user?.email ||
      "SA"
    )
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((x) =>
        x[0]?.toUpperCase()
      )
      .join("");


  return (
    <div className="nk-shell">

      {/* =====================================================
          DESKTOP SIDEBAR
      ====================================================== */}

      <aside
        className={
          `nk-sidebar ${
            collapsed
              ? "nk-sidebar-collapsed"
              : ""
          }`
        }
      >

        <div className="nk-sidebar-brand">
          <div
            className={
              collapsed
                ? "hidden"
                : "block"
            }
          >
            <Logo height={31} />
          </div>

          {collapsed && (
            <div
              className="nk-logo-mark"
              title="Nakshatra Payroll"
            >
              N
            </div>
          )}

          <button
            type="button"
            className="nk-sidebar-collapse"
            onClick={() =>
              setCollapsed(
                (v) => !v
              )
            }
            aria-label={
              collapsed
                ? "Expand sidebar"
                : "Collapse sidebar"
            }
          >
            {collapsed ? (
              <ChevronRight size={17} />
            ) : (
              <ChevronLeft size={17} />
            )}
          </button>
        </div>


        <nav className="nk-sidebar-nav">

          {visibleNav.map(
            (n) => {
              const Icon =
                n.icon;

              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  title={
                    collapsed
                      ? n.label
                      : undefined
                  }
                  data-testid={
                    `nav-${n.label
                      .toLowerCase()
                      .replace(
                        / /g,
                        "-"
                      )}`
                  }
                  className={
                    ({
                      isActive,
                    }) =>
                      `nk-sidebar-link ${
                        isActive
                          ? "nk-sidebar-link-active"
                          : ""
                      }`
                  }
                >
                  <span className="nk-sidebar-icon">
                    <Icon size={18} />
                  </span>

                  {!collapsed && (
                    <span className="nk-sidebar-label">
                      {n.label}
                    </span>
                  )}
                </NavLink>
              );
            }
          )}

        </nav>


        <div className="nk-sidebar-footer">

          <div
            className={
              `nk-sidebar-user ${
                collapsed
                  ? "justify-center"
                  : ""
              }`
            }
          >

            <div className="nk-avatar">
              {initials || "SA"}
            </div>

            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div
                  className="text-sm font-semibold text-slate-900 truncate"
                  data-testid="sidebar-user-name"
                >
                  {user?.name ||
                    user?.email}
                </div>

                <div className="text-[11px] text-slate-400 capitalize truncate">
                  {roleLabel}
                </div>
              </div>
            )}

            {!collapsed && (
              <button
                onClick={doLogout}
                data-testid="logout-btn"
                className="nk-sidebar-logout"
                aria-label="Logout"
              >
                <LogOut size={17} />
              </button>
            )}

          </div>


          {!collapsed && (
            <div className="nk-developed-by">
              <span>
                Developed by
              </span>

              <span className="bg-white rounded px-1.5 py-0.5 inline-flex">
                <QrewHrLogo height={12} />
              </span>
            </div>
          )}

        </div>

      </aside>


      {/* =====================================================
          MAIN APPLICATION
      ====================================================== */}

      <div className="nk-main">

        {/* Desktop header */}

        <header className="nk-desktop-header">

          <div>
            <div className="text-sm font-semibold text-slate-800">
              Nakshatra Payroll
            </div>

            <div className="text-[11px] text-slate-400">
              Payroll Management System
            </div>
          </div>


          <div className="flex items-center gap-3">

            <div className="relative min-w-[220px]">

              <Building2
                size={15}
                className="absolute left-3 top-2.5 text-slate-400 pointer-events-none"
              />

              <select
                data-testid="global-client-select"
                value={
                  selectedClient
                }
                onChange={(e) =>
                  setSelectedClient(
                    e.target.value
                  )
                }
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-9 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">
                  All Clients
                </option>

                {clients.map(
                  (c) => (
                    <option
                      key={c.id}
                      value={c.id}
                    >
                      {c.name}
                    </option>
                  )
                )}
              </select>

              <ChevronDown
                size={15}
                className="absolute right-3 top-2.5 text-slate-400 pointer-events-none"
              />

            </div>


            <div className="nk-header-user">

              <div className="nk-avatar nk-avatar-small">
                {initials || "SA"}
              </div>

              <div className="hidden xl:block">
                <div className="text-xs font-semibold text-slate-800 max-w-[150px] truncate">
                  {user?.name ||
                    user?.email}
                </div>

                <div className="text-[10px] text-slate-400 capitalize">
                  {roleLabel}
                </div>
              </div>

            </div>

          </div>

        </header>


        {/* Mobile top header */}

        <header className="nk-mobile-header">

          <div className="flex items-center gap-2">

            <button
              type="button"
              onClick={() =>
                setMobileMore(true)
              }
              className="nk-mobile-icon-btn"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>

            <div className="nk-logo-mark nk-logo-mark-mobile">
              N
            </div>

            <div>
              <div className="text-sm font-bold text-slate-900">
                Nakshatra
              </div>

              <div className="text-[9px] text-slate-400 leading-none">
                Payroll Management
              </div>
            </div>

          </div>


          <div className="nk-avatar nk-avatar-small">
            {initials || "SA"}
          </div>

        </header>


        <main className="nk-content">
          {children}
        </main>

      </div>


      {/* =====================================================
          MOBILE BOTTOM NAV
      ====================================================== */}

      <nav className="nk-mobile-bottom-nav">

        {MOBILE_PRIMARY.map(
          (n) => {
            const Icon =
              n.icon;

            const active =
              location.pathname ===
                n.to ||
              (
                n.to !==
                  "/dashboard" &&
                location.pathname.startsWith(
                  `${n.to}/`
                )
              );

            return (
              <button
                type="button"
                key={n.to}
                onClick={() =>
                  navigate(n.to)
                }
                className={
                  `nk-mobile-nav-item ${
                    active
                      ? "nk-mobile-nav-active"
                      : ""
                  }`
                }
              >
                <Icon size={19} />

                <span>
                  {n.label}
                </span>
              </button>
            );
          }
        )}


        <button
          type="button"
          onClick={() =>
            setMobileMore(true)
          }
          className="nk-mobile-nav-item"
        >
          <MoreHorizontal size={20} />

          <span>
            More
          </span>
        </button>

      </nav>


      {/* =====================================================
          MOBILE MORE DRAWER
      ====================================================== */}

      {mobileMore && (
        <div className="nk-mobile-drawer-layer">

          <button
            type="button"
            className="nk-mobile-drawer-overlay"
            onClick={() =>
              setMobileMore(false)
            }
            aria-label="Close menu"
          />

          <div className="nk-mobile-drawer">

            <div className="flex items-center justify-between mb-5">

              <div>
                <div className="font-bold text-slate-900">
                  More
                </div>

                <div className="text-xs text-slate-400 mt-0.5">
                  Nakshatra Payroll
                </div>
              </div>

              <button
                type="button"
                className="nk-mobile-icon-btn"
                onClick={() =>
                  setMobileMore(false)
                }
              >
                <X size={20} />
              </button>

            </div>


            <div className="mb-4">

              <label className="nk-overline block mb-1.5">
                Client
              </label>

              <select
                value={
                  selectedClient
                }
                onChange={(e) =>
                  setSelectedClient(
                    e.target.value
                  )
                }
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white"
              >
                <option value="all">
                  All Clients
                </option>

                {clients.map(
                  (c) => (
                    <option
                      key={c.id}
                      value={c.id}
                    >
                      {c.name}
                    </option>
                  )
                )}
              </select>

            </div>


            <div className="grid grid-cols-2 gap-2">

              {moreNav.map(
                (n) => {
                  const Icon =
                    n.icon;

                  return (
                    <button
                      type="button"
                      key={n.to}
                      onClick={() => {
                        navigate(n.to);
                        setMobileMore(false);
                      }}
                      className="nk-mobile-more-card"
                    >
                      <Icon size={19} />

                      <span>
                        {n.label}
                      </span>
                    </button>
                  );
                }
              )}

            </div>


            <div className="nk-mobile-drawer-user">

              <div className="nk-avatar">
                {initials || "SA"}
              </div>

              <div className="min-w-0 flex-1">

                <div className="font-semibold text-sm truncate">
                  {user?.name ||
                    user?.email}
                </div>

                <div className="text-xs text-slate-400 capitalize">
                  {roleLabel}
                </div>

              </div>


              <button
                type="button"
                onClick={doLogout}
                className="p-2 rounded-lg text-red-500 bg-red-50"
              >
                <LogOut size={17} />
              </button>

            </div>

          </div>

        </div>
      )}

    <OwnerChat/>
    </div>
  );
}
